/**
 * DarAtencao.jsx — Tarefas ativas agrupadas por urgência de deadline.
 * Grupos: Prazo Ultrapassado | Hoje | Amanhã | Depois de amanhã | Sem Deadline
 */

import React, { useEffect, useState } from "react";
import { fetchDarAtencao, fetchProjectManagers, fetchMarcas, fetchHeads } from "../services/api";
import { formatDate } from "../utils/formatDate";

const BUCKETS_ATIVOS = [
  "ROADMAP", "NEGOCIAR DEADLINE", "ONGOING",
  "AGUARDO FEEDBACK", "PARA APROVAÇÃO INTERNA",
  "PARA APROVAÇÃO EXTERNA", "APROVADO",
];

const GRUPOS_CONFIG = {
  ultrapassado:  { cor: "#dc2626", fundo: "#fff1f2", borda: "#fecaca", icone: "🔴" },
  hoje:          { cor: "#ea580c", fundo: "#fff7ed", borda: "#fed7aa", icone: "🟠" },
  amanha:        { cor: "#d97706", fundo: "#fffbeb", borda: "#fde68a", icone: "🟡" },
  depois_amanha: { cor: "#2563eb", fundo: "#eff6ff", borda: "#bfdbfe", icone: "🔵" },
  sem_deadline:  { cor: "#64748b", fundo: "#f8fafc", borda: "#e2e8f0", icone: "⚪" },
};

export default function DarAtencao({ roleEfetivo }) {
  const [data, setData]         = useState(null);
  const [pms, setPms]         = useState([]);
  const [heads, setHeads]     = useState([]);
  const [marcas, setMarcas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro]       = useState(null);
  const [filtros, setFiltros] = useState({ pm_id: "", head_id: "", marca: "", bucket: "", sem_responsavel: "" });
  const [expandidos, setExpandidos] = useState({
    ultrapassado: false, hoje: false,
    amanha: false, depois_amanha: false, sem_deadline: false,
  });

  const carregar = (f = filtros) => {
    setLoading(true);
    setErro(null);
    const params = {};
    if (f.pm_id)           params.pm_id           = f.pm_id;
    if (f.head_id)         params.head_id         = f.head_id;
    if (f.marca)           params.marca           = f.marca;
    if (f.bucket)          params.bucket          = f.bucket;
    if (f.sem_responsavel) params.sem_responsavel = true;
    fetchDarAtencao(params)
      .then(setData)
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProjectManagers().then(setPms).catch(() => {});
    fetchHeads().then(setHeads).catch(() => {});
    fetchMarcas().then(setMarcas).catch(() => {});
    carregar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiltro = (campo, valor) => {
    const novos = { ...filtros, [campo]: valor };
    setFiltros(novos);
    carregar(novos);
  };

  const toggleGrupo = (key) =>
    setExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div>
      <h1 style={styles.titulo}>Dar Atenção</h1>

      {/* Filtros */}
      <div className="filtros-bar" style={styles.filtrosBar}>
        {roleEfetivo !== "pm" && (
          <select
            style={styles.select}
            value={filtros.pm_id}
            onChange={(e) => handleFiltro("pm_id", e.target.value)}
          >
            <option value="">Todos os Project Managers</option>
            {pms.map((pm) => (
              <option key={pm.id} value={pm.id}>{pm.nome}</option>
            ))}
          </select>
        )}
        {heads.length > 0 && (
          <select
            style={styles.select}
            value={filtros.head_id}
            onChange={(e) => handleFiltro("head_id", e.target.value)}
          >
            <option value="">Todas as Heads</option>
            {heads.map((h) => (
              <option key={h.id} value={h.id}>{h.nome}</option>
            ))}
          </select>
        )}
        <select
          style={styles.select}
          value={filtros.marca}
          onChange={(e) => handleFiltro("marca", e.target.value)}
        >
          <option value="">Todas as marcas</option>
          {marcas.map((m) => (
            <option key={m.plan_id} value={m.plan_id}>{m.plan_title}</option>
          ))}
        </select>
        <select
          style={styles.select}
          value={filtros.bucket}
          onChange={(e) => handleFiltro("bucket", e.target.value)}
        >
          <option value="">Todos os Buckets</option>
          {BUCKETS_ATIVOS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select
          style={styles.select}
          value={filtros.sem_responsavel}
          onChange={(e) => handleFiltro("sem_responsavel", e.target.value)}
        >
          <option value="">Todos os responsáveis</option>
          <option value="1">Sem responsável</option>
        </select>
        {data && (
          <span style={styles.totalBadge}>
            {data.total} tarefa{data.total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}

      {loading ? (
        <p style={styles.loading}>A carregar…</p>
      ) : (
        <div style={styles.grupos}>
          {data.grupos.map((grupo) => {
            const cfg      = GRUPOS_CONFIG[grupo.key];
            const n        = grupo.tarefas.length;
            const aberto   = expandidos[grupo.key];

            return (
              <div key={grupo.key} style={{ ...styles.grupoCard, borderColor: cfg.borda }}>
                {/* Cabeçalho clicável */}
                <button
                  style={{ ...styles.grupoHeader, backgroundColor: cfg.fundo, borderBottomColor: aberto ? cfg.borda : "transparent" }}
                  onClick={() => toggleGrupo(grupo.key)}
                >
                  <div style={styles.grupoHeaderLeft}>
                    <span style={styles.grupoIcone}>{cfg.icone}</span>
                    <span style={{ ...styles.grupoLabel, color: cfg.cor }}>{grupo.label}</span>
                    <span style={{ ...styles.grupoBadge, backgroundColor: cfg.cor }}>{n}</span>
                  </div>
                  <span style={{ ...styles.chevron, color: cfg.cor }}>
                    {aberto ? "▲" : "▼"}
                  </span>
                </button>

                {/* Tabela */}
                {aberto && (
                  n === 0 ? (
                    <p style={styles.semTarefas}>Nenhuma tarefa neste grupo.</p>
                  ) : (
                    <div className="table-scroll">
                    <table style={styles.tabela}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Marca</th>
                          <th style={styles.th}>PM</th>
                          <th style={styles.th}>Tarefa</th>
                          <th style={styles.th}>Bucket</th>
                          <th style={styles.th}>Deadline</th>
                          <th style={styles.th}>Responsável</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.tarefas.map((t) => (
                          <tr key={t.task_id} style={styles.tr}>
                            <td style={styles.tdMarca}>{t.marca}</td>
                            <td style={styles.td}>
                              {t.pm_nomes && t.pm_nomes.length > 0
                                ? t.pm_nomes.join(", ")
                                : <span style={styles.semResponsavelTag}>Sem PM</span>}
                            </td>
                            <td style={styles.td}>{t.title}</td>
                            <td style={styles.td}>
                              <span style={styles.bucketTag}>{t.bucket_name}</span>
                            </td>
                            <td style={{ ...styles.td, fontWeight: 500, color: cfg.cor }}>
                              {formatDate(t.due_date)}
                            </td>
                            <td style={styles.td}>
                              {t.responsavel
                                ? t.responsavel
                                : <span style={styles.semResponsavelTag}>Sem responsável</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  titulo: { fontSize: 24, fontWeight: 700, marginBottom: 20, color: "#1e293b" },
  filtrosBar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 24 },
  select: {
    padding: "9px 14px", border: "1px solid #e2e8f0", borderRadius: 6,
    fontSize: 13, backgroundColor: "#fff", color: "#374151", cursor: "pointer",
    minWidth: 200,
  },
  totalBadge: {
    marginLeft: "auto", fontSize: 13, color: "#64748b", fontWeight: 500,
  },
  grupos: { display: "flex", flexDirection: "column", gap: 10 },
  grupoCard: {
    border: "1px solid",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  grupoHeader: {
    width: "100%", display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "12px 16px",
    border: "none", borderBottom: "1px solid", cursor: "pointer",
    textAlign: "left",
  },
  grupoHeaderLeft: { display: "flex", alignItems: "center", gap: 10 },
  grupoIcone: { fontSize: 14 },
  grupoLabel: { fontSize: 14, fontWeight: 700 },
  grupoBadge: {
    color: "#fff", fontSize: 11, fontWeight: 700,
    borderRadius: 10, padding: "2px 8px", minWidth: 22, textAlign: "center",
  },
  chevron: { fontSize: 11, fontWeight: 700 },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "8px 14px",
    backgroundColor: "#f8fafc", fontWeight: 600,
    borderBottom: "1px solid #e2e8f0", color: "#64748b",
    fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em",
  },
  tr: { borderBottom: "1px solid #f1f5f9" },
  tdMarca: { padding: "9px 14px", color: "#374151", fontWeight: 600, whiteSpace: "nowrap" },
  td: { padding: "9px 14px", color: "#374151" },
  bucketTag: {
    display: "inline-block", padding: "2px 8px",
    background: "#f1f5f9", color: "#475569",
    borderRadius: 4, fontSize: 11, fontWeight: 500,
  },
  semTarefas: { padding: "16px", color: "#94a3b8", fontSize: 13, margin: 0 },
  semResponsavelTag: {
    display: "inline-block", padding: "2px 8px",
    background: "#f1f5f9", color: "#94a3b8",
    borderRadius: 4, fontSize: 11, fontWeight: 500, fontStyle: "italic",
  },
  loading: { color: "#64748b", marginTop: 40 },
  erro: {
    background: "#fef2f2", border: "1px solid #fca5a5",
    borderRadius: 6, padding: 16, color: "#dc2626", marginBottom: 16,
  },
};
